import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { 
  Upload, LogIn, FileText, Check, X, Loader2, Users, DollarSign, 
  TrendingUp, BookOpen, Calendar, Filter, Search, ChevronDown,
  Eye, Mail, Phone, Clock, Award, BarChart3, PieChart, ArrowUpRight,
  RefreshCw, Download, Bookmark
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Metrics state
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilter, setUserFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Upload state
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsLoggedIn(true);
    }
  }, []);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadMetrics();
      loadUsers();
    }
  }, [isLoggedIn, userFilter]);

  const loadMetrics = async () => {
    setMetricsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      toast.error('Error al cargar métricas');
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users?filter_type=${userFilter}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      toast.error('Error al cargar usuarios');
    } finally {
      setUsersLoading(false);
    }
  };

  const [accessLoadingId, setAccessLoadingId] = useState(null);

  const toggleAccess = async (user) => {
    const grant = !user.hasPaid;
    setAccessLoadingId(user.id);
    try {
      const token = localStorage.getItem('admin_token');
      const endpoint = grant ? 'grant-access' : 'revoke-access';
      await axios.post(`${API}/admin/${endpoint}`, { user_id: user.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(grant ? `Acceso otorgado a ${user.name || user.email}` : `Acceso quitado a ${user.name || user.email}`);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, hasPaid: grant } : u));
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
      toast.error(error.response?.data?.detail || 'Error al actualizar acceso');
    } finally {
      setAccessLoadingId(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsLoggedIn(true);
        toast.success('¡Bienvenido, administrador!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Solo se permiten archivos PDF');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      toast.error('Selecciona un archivo PDF primero');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/upload-book`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.success('¡PDF subido exitosamente!');
        setUploadedFile(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setMetrics(null);
    setUsers([]);
    toast.success('Sesión cerrada');
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phone?.toLowerCase().includes(query)
    );
  });

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="inline-block bg-amber-600/20 p-4 rounded-full mb-4">
                <LogIn size={32} className="text-amber-500" />
              </div>
              <h1 className="text-2xl font-serif font-bold text-white">Panel de Administrador</h1>
              <p className="text-gray-400 mt-2">Raíz Lecturas</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Usuario</label>
                <Input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  placeholder="usuario"
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  required
                  data-testid="admin-username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Contraseña</label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  required
                  data-testid="admin-password"
                />
              </div>
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="admin-login-btn"
              >
                {loginLoading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <LogIn size={20} />
                    Ingresar
                  </>
                )}
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-700/50 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-serif font-bold text-white">Panel de Administrador</h1>
              <p className="text-gray-400 text-sm">Raíz Lecturas</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadMetrics}
                className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
                title="Actualizar datos"
              >
                <RefreshCw size={20} className={metricsLoading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all text-sm"
              >
                Ver Sitio
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-all text-sm"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-gray-900/50 border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'users', label: 'Usuarios', icon: Users },
              { id: 'upload', label: 'Subir PDF', icon: Upload },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-amber-500 border-amber-500 bg-amber-500/5' 
                    : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/50'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {metricsLoading && !metrics ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-amber-500" size={40} />
              </div>
            ) : metrics ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gray-800/50 border-gray-700/50">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <Users className="text-blue-400" size={24} />
                        <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full">
                          +{metrics.registration.today} hoy
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-white">{metrics.summary.totalUsers}</p>
                      <p className="text-gray-400 text-sm">Usuarios totales</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-800/50 border-gray-700/50">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <DollarSign className="text-green-400" size={24} />
                        <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-1 rounded-full">
                          {metrics.summary.paidUsers} ventas
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-white">${metrics.summary.totalRevenue}</p>
                      <p className="text-gray-400 text-sm">Ingresos totales</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-800/50 border-gray-700/50">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <TrendingUp className="text-amber-400" size={24} />
                        <ArrowUpRight className="text-green-400" size={16} />
                      </div>
                      <p className="text-3xl font-bold text-white">{metrics.summary.conversionRate}%</p>
                      <p className="text-gray-400 text-sm">Tasa de conversión</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-800/50 border-gray-700/50">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <BookOpen className="text-purple-400" size={24} />
                      </div>
                      <p className="text-3xl font-bold text-white">{metrics.reading.avgPage}</p>
                      <p className="text-gray-400 text-sm">Página promedio</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Registration Stats */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-gray-800/50 border-gray-700/50">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <Calendar className="text-amber-500" size={20} />
                        Registros
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                          <span className="text-gray-300">Hoy</span>
                          <span className="text-white font-bold text-xl">{metrics.registration.today}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                          <span className="text-gray-300">Esta semana</span>
                          <span className="text-white font-bold text-xl">{metrics.registration.thisWeek}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                          <span className="text-gray-300">Este mes</span>
                          <span className="text-white font-bold text-xl">{metrics.registration.thisMonth}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-800/50 border-gray-700/50">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <PieChart className="text-amber-500" size={20} />
                        Distribución de Usuarios
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-gray-300">Usuarios pagados</span>
                          </div>
                          <span className="text-white font-bold">{metrics.summary.paidUsers}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-green-400 h-3 rounded-full transition-all"
                            style={{ width: `${metrics.summary.conversionRate}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                            <span className="text-gray-300">Usuarios gratuitos</span>
                          </div>
                          <span className="text-white font-bold">{metrics.summary.freeUsers}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Reading Progress */}
                <Card className="bg-gray-800/50 border-gray-700/50">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <BookOpen className="text-amber-500" size={20} />
                      Progreso de Lectura
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                        <p className="text-2xl font-bold text-white">{metrics.reading.usersStarted}</p>
                        <p className="text-gray-400 text-sm">Empezaron a leer</p>
                      </div>
                      <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                        <p className="text-2xl font-bold text-amber-400">{metrics.reading.usersPastPreview}</p>
                        <p className="text-gray-400 text-sm">Pasaron preview (30+)</p>
                      </div>
                      <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                        <p className="text-2xl font-bold text-blue-400">{metrics.reading.usersHalfway}</p>
                        <p className="text-gray-400 text-sm">Mitad del libro (88+)</p>
                      </div>
                      <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                        <p className="text-2xl font-bold text-green-400">{metrics.reading.usersCompleted}</p>
                        <p className="text-gray-400 text-sm">Completaron (170+)</p>
                      </div>
                      <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                        <p className="text-2xl font-bold text-purple-400">{metrics.reading.totalBookmarks}</p>
                        <p className="text-gray-400 text-sm">Marcadores totales</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-400">No se pudieron cargar las métricas</p>
                <button onClick={loadMetrics} className="mt-4 text-amber-500 hover:text-amber-400">
                  Reintentar
                </button>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Buscar por nombre, email o teléfono..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div className="flex gap-2">
                {[
                  { id: 'all', label: 'Todos' },
                  { id: 'paid', label: 'Pagados' },
                  { id: 'free', label: 'Gratuitos' },
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setUserFilter(filter.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      userFilter === filter.id
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Users count */}
            <p className="text-gray-400 text-sm">
              Mostrando {filteredUsers.length} de {totalUsers} usuarios
            </p>

            {/* Users Table */}
            {usersLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-amber-500" size={40} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Usuario</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm hidden md:table-cell">Teléfono</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium text-sm">Página</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium text-sm">Estado</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm hidden lg:table-cell">Registro</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => (
                      <tr 
                        key={user.id || index} 
                        className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                              {user.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <span className="text-white font-medium">{user.name || 'Sin nombre'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-300 text-sm">{user.email}</span>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-gray-400 text-sm">{user.phone || '-'}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-amber-400 font-medium">{user.lastPage || 1}</span>
                          <span className="text-gray-500 text-sm">/176</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {user.hasPaid ? (
                            <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-xs font-semibold px-2 py-1 rounded-full">
                              <Check size={12} />
                              Pagado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-gray-500/20 text-gray-400 text-xs font-semibold px-2 py-1 rounded-full">
                              Gratis
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          <span className="text-gray-500 text-sm">
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-AR') : '-'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => toggleAccess(user)}
                            disabled={accessLoadingId === user.id}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50 ${
                              user.hasPaid
                                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            }`}
                          >
                            {accessLoadingId === user.id
                              ? '...'
                              : user.hasPaid ? 'Quitar acceso' : 'Dar acceso'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredUsers.length === 0 && (
                  <div className="text-center py-10">
                    <Users className="mx-auto text-gray-600 mb-3" size={40} />
                    <p className="text-gray-400">No se encontraron usuarios</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gray-800/50 border-gray-700/50">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-amber-600/20 p-3 rounded-full">
                    <Upload size={24} className="text-amber-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Actualizar Libro PDF</h2>
                    <p className="text-gray-400">Subí la versión final o actualizada del libro</p>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                  <p className="text-blue-400 text-sm">
                    📝 <strong>Nota:</strong> El PDF se actualizará automáticamente para todos los lectores.
                  </p>
                </div>

                {/* Drop Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                    uploadedFile 
                      ? 'border-green-500 bg-green-500/10' 
                      : 'border-gray-600 hover:border-amber-500 hover:bg-amber-500/5'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />

                  {uploadedFile ? (
                    <div className="flex flex-col items-center">
                      <Check size={48} className="text-green-500 mb-4" />
                      <p className="text-white font-semibold">{uploadedFile.name}</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                        }}
                        className="mt-4 text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                      >
                        <X size={16} />
                        Quitar archivo
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <FileText size={48} className="text-gray-500 mb-4" />
                      <p className="text-white font-semibold">Arrastra tu PDF aquí</p>
                      <p className="text-gray-400 text-sm mt-1">o haz clic para seleccionar</p>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                {uploadedFile && (
                  <button
                    onClick={handleUpload}
                    disabled={uploadLoading}
                    className="w-full mt-6 bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white px-6 py-4 rounded-lg font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="upload-btn"
                  >
                    {uploadLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload size={20} />
                        Subir PDF
                      </>
                    )}
                  </button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
